import type { Customer, Warranty, Job, User, Ticket } from '@/types/model'
import { setupApp } from '@/globals/app.ts'
import type { ParseData, InputLabelValue, ResAxios, TableData, ContentData } from '@/types'
import { useJobStore } from '@/stores/app/job.store'
import type { Router } from 'vue-router'
import { useModalStore } from '@/stores/modal.store.ts'
import { getTimeText } from '@/globals/time.ts'
import { toClipboard } from '@soerenmartius/vue3-clipboard'
import { formatCurrency } from '@/globals/number.ts'

type TypeData = 'data' | 'create' | 'edit'

export interface FormDataJob {
  job: Job
  customer: Customer
  warranty: Warranty
}

// Pastikan allowedJobKeys strongly typed
const allowedJobKeys: ReadonlyArray<keyof Job> = [
  'name',
  'note',
  'accessories',
  'labour',
  'transport',
  'quality_report',
  'quality_label',
  'warranty_label',
  'job_label',
  'repair_at',
  'collection_at',
  'actual_start_at',
  'actual_end_at',
  'service_info',
  'repair_info',
  'customer_name',
  'phone',
  'phone2',
  'phone3',
  'address',
  'email',
  'customer_type',
  'customer_types',
  'tax_id',
  'model',
  'models',
  'serial',
  'warranty_no',
  'purchase_date',
  'warranty_type',
  'job_type',
  'job_types',
  'status',
  'states',
  'handle_by',
  'branch_service',
  'branch_services',
  'ticket',
  'tickets',
  'created_at',
  'updated_at',
  'invoices',
] as const

export const parseDataJob = async (
  type: TypeData,
  id: number = 0,
  query: string = '',
): Promise<ParseData<Job> | null> => {
  const { loadJob, loadJobCreate, loadJobData } = useJobStore()

  // dapatkan data dari mana
  const action: Record<TypeData, () => Promise<ResAxios<Job>>> = {
    data: () => loadJob(query),
    create: () => loadJobCreate(query),
    edit: () => loadJobData(id),
  }

  // res
  const res = await action[type]()

  // jika sukses
  if (res.data.status === 'success') {
    // simplify
    const resDataUser = res.data.user as User
    const content = (res.data.content as ContentData) ?? null
    const data = res.data.data
      ? type === 'data'
        ? (res.data.data as TableData<Job>)
        : (res.data.data as Job)
      : null

    // atur breadcrumb and sidebar
    const breadcrumbs = content?.breadcrumbs ?? undefined
    const sidebar = content?.sub_pages ?? null

    // setup app
    setupApp(resDataUser, breadcrumbs, sidebar)

    // customer type
    const resDataCustomerTypes = res.data.customer_types

    // branch service
    const resDataBranchServices = res.data.branch_services

    // job type
    const resDataJobTypes = res.data.job_types

    // job status
    const resDataJobStates = res.data.job_states

    // categories
    const categories = res.data.categories ?? undefined

    // branches
    const branches = resDataBranchServices
      ? (resDataBranchServices.map((branchService) => ({
          value: branchService.code,
          label: branchService.name,
        })) as InputLabelValue[])
      : null

    // branch services
    const branch_services = resDataBranchServices
      ? (resDataBranchServices.map((branchService) => ({
          value: branchService.id,
          label: branchService.name,
        })) as InputLabelValue[])
      : null

    // job types
    const job_types = resDataJobTypes
      ? (resDataJobTypes.map((jobType) => ({
          value: jobType.id,
          label: jobType.name,
          color: jobType.color,
        })) as InputLabelValue[])
      : null

    // job states
    const job_states = resDataJobStates
      ? (resDataJobStates.map((jobStatus) => ({
          value: jobStatus.id,
          label: jobStatus.name,
          color: jobStatus.color,
        })) as InputLabelValue[])
      : null

    // job lists
    const jobLists = { branches, branch_services, job_types, job_states }

    // customer type lists
    const customerTypeLists: InputLabelValue[] | null = resDataCustomerTypes
      ? (resDataCustomerTypes.map((custType) => ({
          value: custType.id,
          label: custType.name,
          color: custType.color,
        })) as InputLabelValue[])
      : null

    const ticket: Ticket | undefined = (res.data.ticket as Ticket) ?? undefined

    return {
      data,
      content,
      categories,
      customerTypeLists,
      jobLists,
      ticket,
    }
  }

  return null
}

export const sendDataJob = async (
  type: TypeData,
  form: FormDataJob,
  router: Router,
  onRefresh?: () => void,
): Promise<void | Record<string, string[]> | null> => {
  const { addJob, editJob } = useJobStore()
  const { showModal } = useModalStore()

  const job: Job = {
    ...form.job,
    customer_name: form.customer.name,
    customer_type: form.customer.type,
    phone: form.customer.phone,
    phone2: form.customer.phone2,
    phone3: form.customer.phone3,
    address: form.customer.address,
    email: form.customer.email,
    tax_id: form.customer.tax_id,
    model: form.warranty.model,
    serial: form.warranty.serial,
    warranty_no: form.warranty.warranty_no,
    warranty_type: form.warranty.type,
  }

  const res = type === 'create' ? await addJob(job) : await editJob(job)

  showModal(res.data.status, res.data.message, res.data.status)

  if (res.data.status === 'success') {
    if (type === 'edit' && onRefresh) {
      onRefresh()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } else {
      const jobId: number = res.data.data.id as number
      if (
        router.currentRoute.value.name === 'job-edit' &&
        router.currentRoute.value.params.id == String(jobId)
      ) {
        await router.push({ name: 'job-edit', params: { id: jobId } }).then(() => {
          window.location.reload()
          window.scrollTo({ top: 0, behavior: 'smooth' })
        })
      } else {
        await router.push({ name: 'job-edit', params: { id: jobId } })
      }
    }
  }

  if (res.data.status === 'error') {
    return res.data.errors as Record<string, string[]> | null
  }
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export const assignJobFields = (target: Job, source: unknown) => {
  const sourceRecord = source as Record<string, unknown>
  for (const key of allowedJobKeys) {
    if (key in sourceRecord) {
      target[key] = sourceRecord[key] as any
    }
  }
}
/* eslint-enable @typescript-eslint/no-explicit-any */

export const joinPartNames = (job: Job): string => {
  const names = job.parts?.map((p) => p.name) ?? []

  if (names.length === 0) return ''
  if (names.length === 1) return String(names[0])
  if (names.length === 2) return `${names[0]} & ${names[1]}`
  return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`
}

export const copyEstimateToClipboard = async (job: Job, estimate: number = 0) => {
  const { showModal } = useModalStore()

  const parts: string = joinPartNames(job)
  const text =
    'Selamat ' +
    getTimeText() +
    ', saya Erwin dari Panasonic Service\n\n' +
    'Untuk unit *' +
    (job.models?.desc ?? 'Perbaikan') +
    '* dengan model *' +
    job.model +
    '* atas nama *' +
    job.customer_name +
    '* sudah selesai cek, ternyata unit mengalami kerusakan pada bagian *' +
    parts +
    '*, untuk estimasi biaya perbaikannya *' +
    formatCurrency(estimate * 1.11) +
    '* bila setuju langsung kami proses unitnya 🙏\n\n' +
    '*estimasi hanya bersifat sementara bila ada info lebih lanjut kami kabari kembali\n\n' +
    '*Panasonic Service Cirebon*\n' +
    'Jl. Kalijaga No.43, Pegambiran, Lemahwungkuk, Kota Cirebon\n' +
    '0231-232183'

  const clipboard = await navigator.clipboard.readText()

  if (clipboard !== text) {
    try {
      await toClipboard(text)
      showModal('success', 'Sukses menyalin clipboard')
    } catch {
      showModal('error', 'Gagal menyalin clipboard')
    }
  }
}

export const copyWorkDoneToClipboard = async (job: Job, estimate: number = 0) => {
  const { showModal } = useModalStore()

  const outWarrantyText: string =
    job.warranty_type === 0
      ? 'untuk total biaya perbaikannya *' + formatCurrency(estimate * 1.11) + '* 🙏, '
      : ''
  const text =
    'Selamat ' +
    getTimeText() +
    ', saya Erwin dari Panasonic Service\n\n' +
    'Untuk unit *' +
    (job.models?.desc ?? 'Perbaikan') +
    '* dengan model *' +
    job.model +
    '* atas nama *' +
    job.customer_name +
    '* sudah selesai diperbaiki, ' +
    outWarrantyText +
    ' unit bisa diambil pada jam kerja kami di\n\n' +
    'Jam operasional,\n' +
    '08.00 - 16.00\n\n' +
    '*Panasonic Service Cirebon*\n' +
    'Jl. Kalijaga No.43, Pegambiran, Lemahwungkuk, Kota Cirebon\n' +
    '0231-232183'

  const clipboard = await navigator.clipboard.readText()

  if (clipboard !== text) {
    try {
      await toClipboard(text)
      showModal('success', 'Sukses menyalin clipboard')
    } catch {
      showModal('error', 'Gagal menyalin clipboard')
    }
  }
}
